import { apiGet, getAuthToken } from '../../Restore/library/helpers/apiHelper';
import cookieHelper from '../../Restore/library/helpers/cookieHelper';

// Short-term user-scoped in-memory cache for status counts to prevent slamming the database on refresh/rapid navigation
const statusCountsCache = new Map();
let cachedCountsStaffId = null;
let cachedCountsToken = null;
const CACHE_TTL_MS = 15000; // 15 seconds

export const invalidateStatusCountsCache = () => {
  statusCountsCache.clear();
  cachedCountsStaffId = null;
  cachedCountsToken = null;
};

if (typeof window !== 'undefined') {
  window.addEventListener('ordersUpdated', invalidateStatusCountsCache);
  window.addEventListener('userLoggedOut', invalidateStatusCountsCache);
  window.addEventListener('permissionsUpdated', invalidateStatusCountsCache);
  window.addEventListener('authStateChanged', invalidateStatusCountsCache);
}

/**
 * Get counts of sleep orders by status
 * @param {Object} params - Query parameters
 * @param {number} [params.month] - Month (1-12)
 * @param {number} [params.year] - Year (e.g., 2025)
 * @param {boolean} [params.allTime] - Whether to get counts for all time
 * @param {string} [params.interpretingPhysicianId] - Interpreting physician ID to filter by
 * @param {string} [params.referringPhysicianId] - Referring physician ID to filter by
 * @returns {Promise<Object>} Status counts data
 */
export const getStatusCounts = async (params = {}) => {
  try {
    const currentToken = getAuthToken();
    const userDetails = cookieHelper.getUserDetails();
    const currentStaffId = userDetails?.staffId || userDetails?.staff_id || '';

    // If logged-in user or token changed, immediately discard previous user's cached counts
    if (cachedCountsStaffId !== currentStaffId || cachedCountsToken !== currentToken) {
      statusCountsCache.clear();
      cachedCountsStaffId = currentStaffId;
      cachedCountsToken = currentToken;
    }

    // Build query string from params
    const queryParams = new URLSearchParams();
    
    if (params.month) {
      queryParams.append('month', params.month);
    }
    
    if (params.year) {
      queryParams.append('year', params.year);
    }
    
    if (params.allTime) {
      queryParams.append('allTime', true);
    }
    
    if (params.interpretingPhysicianId) {
      queryParams.append('interpretingPhysicianId', params.interpretingPhysicianId);
    }
    
    if (params.referringPhysicianId) {
      queryParams.append('referringPhysicianId', params.referringPhysicianId);
    }
    
    const queryString = queryParams.toString();
    const cacheKey = queryString || 'default';

    // Check short-term in-memory cache
    const cached = statusCountsCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp < CACHE_TTL_MS)) {
      return cached.data;
    }

    const url = `api/v1/sleep/orders/status-counts${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiGet(url);
    if (response && response.success !== false) {
      statusCountsCache.set(cacheKey, {
        timestamp: Date.now(),
        data: response
      });
    }
    return response;
  } catch (error) {
    console.error('Error fetching status counts:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message,
      data: {}
    };
  }
};
